"use client";

import { Check, Sparkles, Crown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AccountGateButton } from "@/components/auth/account-gate-button";
import { UpgradeCheckoutButton } from "@/components/billing/upgrade-checkout-button";
import { WaitlistButton } from "@/components/billing/waitlist-button";
import {
  PAYMENTS_ENABLED,
  PREMIUM_PRICE_EUR,
  PREMIUM_PRICE_EUR_YEARLY,
  MAX_PRICE_EUR,
  MAX_PRICE_EUR_YEARLY,
  FREE_SHOWCASE_TICKERS,
} from "@/lib/config";
import { joinWithUnd } from "@/lib/text";
import { cn } from "@/lib/utils";

const showcaseNames = joinWithUnd(FREE_SHOWCASE_TICKERS.map((t) => t.name));

// Zweite Zeile ("Dividenden, Chartanalyse & Konkurrenzanalyse") entfernt (Landingpage-Feedback
// 2026-08-17, Punkt 15/12): Dividenden-Tracking & Co. tauchen bei Pro/Max als eigene Zeilen auf,
// bei Basis gab es dafuer aber gar keine Entsprechung - las sich wie ein eigenstaendiges
// Basis-Feature, obwohl es nur ein (verwirrender) Verweis auf Chart/Dividenden-Ansicht der 4
// gratis einsehbaren Aktien war. Erste Zeile ("komplett einsehbar") deckt das bereits ab.
const BASIS_FEATURES = [
  `${showcaseNames} komplett einsehbar`,
  "Watchlist inkl. Alerts bei Score-Änderungen",
];

/**
 * Landingpage-Feedback Runde 3 (2026-08-17), Punkte 13/14/15/16/17/19 - komplett
 * ueberarbeitet, jede Zeile jetzt bewusst auf max. 2 Zeilen Textlaenge gehalten:
 * - [Punkt 13] Erste Zeile stellt jetzt explizit den Erikonomie-Score als
 *   Einschaetzungs-Basis heraus statt nur "alle Kategorien" aufzuzaehlen. Neue zweite
 *   Zeile (bisher fehlend) macht die neue "Aktienkennzahlen"-Analyse (siehe
 *   HERO_BULLETS in app/page.tsx) auch hier explizit.
 * - [Punkt 14+15] Depot-Röntgenblick-Zeile: "Rebalancing" gestrichen (Detail-Feature,
 *   nicht die Kernaussage dieser Zeile) UND die vorher separate "Tiefgreifende
 *   KI-Depotanalyse inkl. PDF-Export"-Zeile hier eingearbeitet statt als eigene,
 *   fast-doppelte Zeile daneben zu stehen - beide beschreiben laut lib/access.ts
 *   (`hasPremiumFeatureAccess`-Docstring) ohnehin dieselbe Depot-Röntgenblick-
 *   Aggregation (gewichteter Score, Klumpenrisiko, KI-Analyse, PDF-Export), keine
 *   zwei getrennten Produktbereiche.
 * - [Punkt 17] "(inkl. ETFs)" ergänzt - Depot-Röntgenblick berücksichtigt ETF-Positionen
 *   tatsächlich vollwertig (Sektor-/Firmen-Look-through über ETF-Holdings, eigene
 *   TER-Auswertung, siehe lib/portfolio/depot-breakdown.ts + etf-fees-summary.ts),
 *   bisher aber nirgends auf der Landingpage erwähnt.
 * - [Punkt 16+19] "Excel-Upload für Depot" (wenig aussagekräftig) ersetzt durch
 *   Chartanalyse + Konkurrenzanalyse - beides echte, bestehende Tabs auf jeder
 *   Aktienseite (app/[symbol]/_sections/category-tabs.tsx: "chartanalyse"/
 *   "konkurrenzanalyse"), bisher auf keiner Preiskachel erwähnt.
 * `coverageCount` kommt als Prop rein (siehe PricingBundle unten) statt hier direkt
 * berechnet zu werden - diese Datei ist "use client", die Ticker-Zaehlung soll
 * serverseitig bleiben statt die kompletten Ticker-Listen ins Client-Bundle zu ziehen.
 */
function buildProFeatures(coverageCount: number): string[] {
  return [
    `Über ${coverageCount}+ Aktien mit Erikonomie-Score fundiert einschätzen — wächst stetig`,
    "Finanzkennzahlen-Analyse je Aktie (Umsatz, Gewinn, Cashflow)",
    "Depot-Röntgenblick inkl. ETFs: KI-Analyse & PDF-Export",
    "Automatisierte Quartalsbericht-Zusammenfassung je Aktie",
    "Meine News: personalisierte, KI-zusammengefasste Depot-News",
    "Chartanalyse & Konkurrenzanalyse je Aktie",
  ];
}

// Max zeigt alle Pro-Features nochmal konkret ausgeschrieben statt eines vagen
// "Alles aus Pro"-Verweises (Erik 2026-07-23) - plus die exklusiven Features obendrauf.
// Dividenden-Tracking von Pro nach Max verschoben (Landingpage-Feedback 2026-08-17,
// Punkt 15, "bereits final"): backlog.md -> "Ohne Erik umsetzbar" hatte das schon als
// entschiedenen, nur noch nicht umgesetzten Punkt stehen (siehe auch die "ab Max"-
// Banderole auf dem Landingpage-Snippet, die diese Zuordnung bereits vorwegnahm).
// "Excel-Upload für Dividenden" (Landingpage-Feedback Runde 3, Punkt 19: wenig
// aussagekräftig als eigene Zeile) jetzt als Beleg-Import-Detail in die Dividenden-Zeile
// selbst eingearbeitet statt einer eigenen, schwachen dritten Max-Zeile - es gibt aktuell
// kein drittes, wirklich eigenstaendiges Max-exklusives Feature jenseits von
// Dividenden-Tracking und Premium-Aktienanalysen (siehe lib/access.ts:
// hasMaxFeatureAccess/dividenden-tracker-Gating), eine erfundene dritte Zeile waere
// weniger glaubwuerdig als zwei praezise.
// "12-Monats-Prognose" -> "Mehrjahres-Prognose" (Landingpage-Feedback Runde 6, Punkt 3):
// die Dividenden-Hochrechnung im Tool selbst geht per Zeitraum-Regler bis zu 20 Jahre
// (PROJECTION_RANGES in dividend-history-forecast-chart.tsx, DIVIDEND_PROJECTION_YEARS =
// 20 in app/dividenden-tracker/page.tsx) - "12 Monate" unterschaetzte das deutlich. Bewusst
// KEINE konkrete Jahreszahl ("20 Jahre") in der Preiszeile (Erik: das waere wiederum zu
// viel/zu spezifisch fuer diese kurze Zeile) - "Mehrjahres-" bleibt wahrheitsgemaess UND
// kurz. Das nahe 12-Monats-Kalenderfenster bleibt ueber "mit Kalender" weiterhin abgedeckt.
function buildMaxFeatures(coverageCount: number): string[] {
  return [
    ...buildProFeatures(coverageCount),
    "Dividenden-Tracking mit Kalender, Mehrjahres-Prognose & Beleg-Import",
    "Premium Aktienanalysen: exklusive, redaktionell erstellte Tiefenanalysen",
  ];
}

const proPriceLabel = PREMIUM_PRICE_EUR.toFixed(2).replace(".", ",");
const maxPriceLabel = MAX_PRICE_EUR.toFixed(2).replace(".", ",");

/**
 * `tone` (Landingpage-Feedback 2026-08-17, Punkt 12: Pro-Kachel navy, Max-Kachel gold als
 * volle Hintergrundfarbe statt nur dezenter Tönung) - dieselbe FeatureRow wird jetzt auf drei
 * unterschiedlich eingefärbten Kachel-Hintergründen verwendet und braucht deshalb passende
 * Text-/Haekchen-Farben je Hintergrund statt der bisher fest verdrahteten hellen Kachel-Töne.
 * "onGold" nutzt Navy statt des dunkleren `gold-text`-Tons fuer Haekchen/Text (gleiche
 * Navy-auf-Gold-Kombination wie bereits in opengraph-image.tsx bewaehrt), da `gold-text` auf
 * dem volleren `bg-gold` selbst zu wenig Kontrast haette.
 */
function FeatureRow({
  children,
  tone = "light",
}: {
  children: React.ReactNode;
  tone?: "light" | "onNavy" | "onGold";
}) {
  return (
    <li
      className={cn(
        "flex items-start gap-2 text-sm",
        tone === "onNavy" && "text-primary-foreground/85",
        tone === "onGold" && "text-ink/85",
        tone === "light" && "text-ink-secondary",
      )}
    >
      <Check
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          tone === "onNavy" && "text-gold",
          tone === "onGold" && "text-primary",
          tone === "light" && "text-gold-text",
        )}
        aria-hidden="true"
      />
      <span>{children}</span>
    </li>
  );
}

/**
 * Landingpage-Preisbundle (Feedback-Runde 9, "edle Preisbundle-Darstellung", 2026-07-22;
 * dritte Stufe "Max" + Namensschema Basis/Pro/Max ergaenzt 2026-07-23): drei
 * Preis-Kacheln direkt auf der Startseite, hochwertig/dezent im bestehenden Look.
 * `PAYMENTS_ENABLED` ist aktuell `false` (bis auf Weiteres bleibt buchstaeblich alles
 * kostenlos, siehe backlog.md -> "Monetarisierung"). Pro-/Max-Buttons zeigen deshalb
 * statt eines Checkouts einen `WaitlistButton` ([Punkt 88], 2026-08-06) - sammelt
 * parallel schon Leads fuer den Launch, statt Interessenten nur "Bald verfügbar" zu
 * zeigen. Die Basis-Spalte fuehrt weiterhin zur echten, schon heute nutzbaren
 * Registrierung (Watchlist/Alerts), nicht ins Leere. "Pro"/"Max" sind inzwischen echte
 * Tier-Werte (siehe `Tier` in lib/access.ts, Migration 0030) statt reiner
 * Anzeige-Labels. Sobald `PAYMENTS_ENABLED` auf `true` steht, verdrahtet
 * `UpgradeCheckoutButton` beide Buttons mit dem vorbereiteten Stripe-Checkout
 * (app/api/stripe/checkout/route.ts). `coverageCount` (live gezaehlte Aktienzahl, siehe
 * lib/stock-coverage.ts) kommt als Prop vom Server-Component-Aufrufer (page.tsx) rein.
 */
export function PricingBundle({
  isLoggedIn = false,
  coverageCount,
}: {
  isLoggedIn?: boolean;
  coverageCount: number;
}) {
  const proFeatures = buildProFeatures(coverageCount);
  const maxFeatures = buildMaxFeatures(coverageCount);

  return (
    <section className="mx-auto w-full max-w-5xl px-6 py-4">
      {/* Bewusst kein Dunkelblau/Gold-Stil-Baustein mehr an dieser Stelle (Erik
          2026-08-14: "den goldenen Banner einfach umgestalten und unauffälliger machen,
          da dort nicht der Hauptfokus liegen soll") - reiner, ruhiger Fließtext statt
          einer farbigen Flaeche, damit der Slogan nicht mit dem neuen Hero konkurriert.
          Die Dunkelblau/Gold-Bausteine an anderer Stelle (Earnings-Zitat, Portfolio-News)
          bleiben davon unberuehrt. */}
      {/* Slogan vergroessert (text-xl statt text-base) + Disclaimer-Zeile ersatzlos entfernt
          (Landingpage-Feedback 2026-08-17, Punkt 10) - ein ausfuehrlicher Disclaimer steht
          bereits im Footer weiter unten, die verkleinerte/duplizierte Kurzfassung hier war
          redundant. Nochmals vergroessert (text-xl -> text-2xl) + komplett einfarbig
          Erikonomie-Blau statt des vorherigen zweifarbigen Navy/Gold-Splits (Landingpage-
          Feedback Runde 3, 2026-08-17, Punkt 10: "komplett im Erikonomie blau... marginal
          größer") - `text-ink` IST bereits exakt der Erikonomie-Navy-Ton (#16265c, siehe
          globals.css), der zweite Satzteil nutzte bisher stattdessen `text-gold-text`. */}
      <div className="mb-6 text-center">
        <p className="font-serif text-2xl text-ink">
          Wissen ist die beste Geldanlage. Wir liefern die Daten — du entscheidest.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardContent className="flex flex-1 flex-col gap-4 pt-2">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="font-serif text-2xl font-semibold text-ink">Basis</p>
              <p className="font-tabular text-2xl text-ink">0 €</p>
            </div>
            <ul className="flex flex-col gap-2.5">
              {BASIS_FEATURES.map((feature) => (
                <FeatureRow key={feature}>{feature}</FeatureRow>
              ))}
            </ul>
            {isLoggedIn ? (
              <p className="mt-auto flex items-center gap-1.5 text-sm font-medium text-ink">
                <Check className="h-4 w-4 text-gold-text" aria-hidden="true" />
                Du hast bereits ein Konto
              </p>
            ) : (
              <AccountGateButton
                size="sm"
                label="Kostenloses Konto erstellen"
                reason="Erstelle ein kostenloses Konto, um Watchlist und Benachrichtigungen dauerhaft zu speichern."
                className="mt-auto"
              >
                Kostenlos starten
              </AccountGateButton>
            )}
          </CardContent>
        </Card>
        {/* Volle Navy-Flaeche statt nur 3%-Tönung (Landingpage-Feedback 2026-08-17, Punkt 12:
            "Pro Modell dort entsprechend den Hintergrund im Erikonomie Blau") - Pro/Max sollen
            gegenüber Basis bewusst auffälliger wirken. bg-primary/85 statt bg-primary (selber
            Feedback-Tag, Punkt 6): "die Farben... ein wenig transparenter machen... nicht ganz
            so stark kräftig" - dieselbe Farbe, nur etwas durchscheinender statt volldeckend. */}
        <Card className="border-primary bg-primary/85 text-primary-foreground shadow-lg">
          <CardContent className="flex flex-1 flex-col gap-4 pt-2">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-gold" aria-hidden="true" />
                <p className="font-serif text-2xl font-semibold text-primary-foreground">Pro</p>
              </div>
              <div>
                <p className="font-tabular text-2xl text-primary-foreground">
                  {proPriceLabel} €<span className="text-sm font-normal text-primary-foreground/70"> /Monat</span>
                </p>
                <p className="mt-1 text-sm font-medium text-primary-foreground/85">
                  oder {PREMIUM_PRICE_EUR_YEARLY} €/Jahr
                </p>
              </div>
            </div>
            <ul className="flex flex-col gap-2.5">
              {proFeatures.map((feature) => (
                <FeatureRow key={feature} tone="onNavy">
                  {feature}
                </FeatureRow>
              ))}
            </ul>
            <div className="mt-auto flex flex-col gap-1.5">
              {/* Navy-Flaeche mit Gold-Schrift statt vollem Gold-Button (Landingpage-Feedback
                  Runde 9, Punkt 1: Erik wollte "den Button in blau in goldener Schrift" -
                  loest die vorherige volle-Gold-Entscheidung vom 2026-08-14 ab). Spiegelt
                  jetzt das Max-Pendant (Gold-Flaeche mit Navy-Schrift unten): beide Buttons
                  nutzen die eigene Kachel-Grundfarbe als Flaeche + Text in der jeweils
                  anderen Farbe, statt sich farblich zu "vertauschen". Bewusst OHNE Rahmen
                  (Runde 9, Punkt 2 - Erik: "keinen Rahmen um die Button"). `text-gold` (nicht
                  `text-gold-text`) - `--gold-text` ist bewusst der DUNKLERE, nur fuer Text auf
                  HELLEM Untergrund gedachte Ton (siehe globals.css-Kommentar), auf Navy waere
                  er kaum lesbar; `text-gold` ist derselbe helle Ton, den auch das
                  Sparkles-Icon direkt daneben schon auf demselben Navy-Hintergrund nutzt. */}
              {PAYMENTS_ENABLED ? (
                <UpgradeCheckoutButton
                  tier="pro"
                  label="Jetzt freischalten"
                  className="bg-primary text-gold hover:bg-primary/90 focus-visible:ring-gold-text/50"
                />
              ) : (
                <WaitlistButton
                  tier="pro"
                  className="bg-primary text-gold hover:bg-primary/90 focus-visible:ring-gold-text/50"
                />
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="border-gold bg-gold/85 text-ink shadow-lg">
          <CardContent className="flex flex-1 flex-col gap-4 pt-2">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" aria-hidden="true" />
                <p className="font-serif text-2xl font-semibold text-ink">Max</p>
              </div>
              <div>
                <p className="font-tabular text-2xl text-ink">
                  {maxPriceLabel} €<span className="text-sm font-normal text-ink/70"> /Monat</span>
                </p>
                <p className="mt-1 text-sm font-medium text-ink/85">oder {MAX_PRICE_EUR_YEARLY} €/Jahr</p>
              </div>
            </div>
            <ul className="flex flex-col gap-2.5">
              {maxFeatures.map((feature) => (
                <FeatureRow key={feature} tone="onGold">
                  {feature}
                </FeatureRow>
              ))}
            </ul>
            <div className="mt-auto flex flex-col gap-1.5">
              {/* Gold-Button (wie Pro-Kachel) statt Navy (Landingpage-Feedback Runde 8,
                  Punkt 4 - loest die vorherige Navy-Entscheidung vom 2026-08-17 ab: Erik
                  empfand den Navy-Button auf der Gold-Kachel als "farblich verkehrt" - gold
                  ist site-weit die etablierte CTA-Farbe (Hero-"Kostenlos testen", Pro-Button
                  hier direkt daneben), ein abweichender Navy-Button auf ausgerechnet der
                  hoechsten Preisstufe wirkte dadurch weniger wie DER Haupt-Call-to-Action als
                  ein zweitrangiger. Bewusst OHNE Rahmen (Runde 9, Punkt 2 - Erik: "keinen
                  Rahmen um die Button") - Text/Flaeche-Kontrast (Navy-Text auf Gold) reicht
                  fuer die Abgrenzung von der Kachel bereits aus. */}
              {PAYMENTS_ENABLED ? (
                <UpgradeCheckoutButton
                  tier="max"
                  label="Jetzt freischalten"
                  className="bg-gold text-ink hover:bg-gold/90 focus-visible:ring-primary/50"
                />
              ) : (
                <WaitlistButton
                  tier="max"
                  className="bg-gold text-ink hover:bg-gold/90 focus-visible:ring-primary/50"
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
